import { FilteringMetaData, AssortmentData, AssortmentRecord, Material, AssortmentClusterAction } from 'buyplan-common';
import { uniq } from 'lodash';
import * as assortmentService from '../services/assortmentService';
import { getActiveFiltersDefaultFromMeta } from '../services/filterOptions';
import * as materialService from '../services/materialService';
import { PageView } from '../constants/appConfig';
import {
    SET_ASSORTMENT,
    GET_ASSORTMENT_META,
    GET_ASSORTMENT_META_SUCCESS,
    GET_ASSORTMENT_META_FAILURE,
    ADD_ASSORTMENT_RECORD,
    ADD_ASSORTMENT_RECORD_FAILURE,
    ADD_ASSORTMENT_RECORD_SUCCESS,
    REMOVE_ASSORTMENT_RECORD,
    REMOVE_ASSORTMENT_RECORD_FAILURE,
    REMOVE_ASSORTMENT_RECORD_SUCCESS,
    UPDATE_CLUSTERS_ASSORTMENT_RECORDS,
    UPDATE_CLUSTERS_ASSORTMENT_RECORDS_FAILURE,
    UPDATE_CLUSTERS_ASSORTMENT_RECORDS_SUCCESS,
    UPDATE_ASSORTMENT_CLUSTER,
    UPDATE_ASSORTMENT_CLUSTER_SUCCESS,
    UPDATE_ASSORTMENT_CLUSTER_FAILURE,
    UPDATE_ASSORTMENT_MATERIAL,
    UPDATE_ASSORTMENT_MATERIAL_SUCCESS,
    UPDATE_ASSORTMENT_MATERIAL_FAILURE,
} from './actionTypes';
import { setActiveMainFilters } from './user';
import { AnyDispatch, GetRootState } from './types';

// SET ASSORTMENT
export const setAssortmentData = (data: AssortmentData, page: number) =>
    ({
        type: SET_ASSORTMENT,
        data,
        page,
    } as const);

export type SetAssortmentDataAction = ReturnType<typeof setAssortmentData>;

// GET ASSORTMENT META
export const getAssortmentMetaSuccess = (metaData: FilteringMetaData, channelId: number) =>
    ({ type: GET_ASSORTMENT_META_SUCCESS, metaData, channelId, view: PageView.assortment } as const);

export type GetAssortmentMetaSuccessAction = ReturnType<typeof getAssortmentMetaSuccess>;

const getAssortmentMetaFailure = (error: Error) => ({ type: GET_ASSORTMENT_META_FAILURE, error } as const);

export type GetAssortmentMetaFailureAction = ReturnType<typeof getAssortmentMetaFailure>;

export const getAssortmentMetaData =
    (channelId: number, includeSubFilters = true, filterTypes = ['partner', 'category', 'division'], signal?: AbortSignal) =>
    async (dispatch: AnyDispatch, getState: GetRootState) => {
        const { activeFilters } = getState().user.settings;
        const { mainFilters } = activeFilters[`${channelId}|${PageView.assortment}`] ?? { mainFilters: {}, subFilters: {} };
        try {
            dispatch({ type: GET_ASSORTMENT_META });
            const { data: metaData } = await assortmentService.getMetaData(
                filterTypes,
                { ...mainFilters },
                includeSubFilters,
                signal
            );
            dispatch(getAssortmentMetaSuccess(metaData, channelId));
            dispatch(
                setActiveMainFilters(getActiveFiltersDefaultFromMeta(mainFilters, metaData), channelId, PageView.assortment)
            );
        } catch (err: unknown) {
            dispatch(getAssortmentMetaFailure(err as Error));
        }
    };
export type GetAssortmentMetaAction = { type: typeof GET_ASSORTMENT_META };

export const updateAssortmentClusterFailure = ({
    error,
    action,
    materialCode,
    clusterId,
    clusterAssortmentStores,
}: {
    error: Error;
    action: AssortmentClusterAction;
    materialCode: string;
    clusterId: string;
    clusterAssortmentStores: { storeId: string; clusterId: string; storeNumber: string }[];
}) =>
    ({
        type: UPDATE_ASSORTMENT_CLUSTER_FAILURE,
        error,
        clusterAssortmentStores,
        clusterId,
        action,
        materialCode,
    } as const);

export type UpdateAssortmentClusterFailureAction = ReturnType<typeof updateAssortmentClusterFailure>;

export const updateAssortmentClusterSuccess = ({
    action,
    materialCode,
    clusterId,
    clusterAssortmentStores,
}: {
    action: AssortmentClusterAction;
    materialCode: string;
    clusterId: string;
    clusterAssortmentStores: { storeId: string; clusterId: string; storeNumber: string }[];
}) => ({ type: UPDATE_ASSORTMENT_CLUSTER_SUCCESS, clusterAssortmentStores, clusterId, action, materialCode } as const);

export type UpdateAssortmentClusterSuccessAction = ReturnType<typeof updateAssortmentClusterSuccess>;

export const updateAssortmentCluster =
    ({
        channelId,
        action,
        materialCode,
        clusterId,
        clusterAssortmentStores,
    }: {
        channelId: number;
        action: AssortmentClusterAction;
        materialCode: string;
        clusterId: string;
        clusterAssortmentStores: { storeId: string; clusterId: string; storeNumber: string }[];
    }) =>
    async (dispatch: AnyDispatch, getState: GetRootState) => {
        try {
            const { activeFilters } = getState().user.settings;
            const { mainFilters } = activeFilters[`${channelId}|${PageView.assortment}`] ?? {
                mainFilters: {},
                subFilters: {},
            };

            dispatch({ type: UPDATE_ASSORTMENT_CLUSTER, action, clusterId, materialCode, clusterAssortmentStores });
            await assortmentService.updateClustersAssortmentRecords(
                { ...mainFilters },
                {
                    action,
                    materialCode,
                    clusterIds: [clusterId],
                }
            );
            dispatch(updateAssortmentClusterSuccess({ action, materialCode, clusterAssortmentStores, clusterId }));
        } catch (err: unknown) {
            dispatch(
                updateAssortmentClusterFailure({
                    error: err as Error,
                    action,
                    materialCode,
                    clusterId,
                    clusterAssortmentStores,
                })
            );
        }
    };

export type UpdateAssortmentClusterAction = {
    type: typeof UPDATE_ASSORTMENT_CLUSTER;
    action: AssortmentClusterAction;
    materialCode: string;
    clusterId: string;
    clusterAssortmentStores: { storeId: string; clusterId: string; storeNumber: string }[];
};

export const updateClusterAssortmentRecordsFailure = (
    error: Error,
    action: AssortmentClusterAction,
    materialCode: string,
    clusterAssortmentStores: { storeId: string; clusterId: string; storeNumber: string }[]
) =>
    ({
        type: UPDATE_CLUSTERS_ASSORTMENT_RECORDS_FAILURE,
        error,
        action,
        materialCode,
        clusterAssortmentStores,
    } as const);

export type UpdateClusterAssortmentRecordsAction = {
    type: typeof UPDATE_CLUSTERS_ASSORTMENT_RECORDS;
};

export type UpdateClusterAssortmentRecordsFailureAction = ReturnType<typeof updateClusterAssortmentRecordsFailure>;

export const updateClusterAssortmentRecordsSuccess = (
    action: AssortmentClusterAction,
    materialCode: string,
    clusterAssortmentStores: { storeNumber: string; storeId: string; clusterId: string }[]
) => ({ type: UPDATE_CLUSTERS_ASSORTMENT_RECORDS_SUCCESS, action, materialCode, clusterAssortmentStores } as const);

export type UpdateClusterAssortmentRecordsSuccessAction = ReturnType<typeof updateClusterAssortmentRecordsSuccess>;

export const updateClustersAssortmentRecords =
    ({
        channelId,
        action,
        materialCode,
        clusterAssortmentStores,
    }: {
        channelId: number;
        action: AssortmentClusterAction;
        materialCode: string;
        clusterAssortmentStores: { storeNumber: string; storeId: string; clusterId: string }[];
    }) =>
    async (dispatch: AnyDispatch, getState: GetRootState) => {
        try {
            dispatch({ type: UPDATE_CLUSTERS_ASSORTMENT_RECORDS });
            const { activeFilters } = getState().user.settings;
            const { mainFilters } = activeFilters[`${channelId}|${PageView.assortment}`] ?? {
                mainFilters: {},
                subFilters: {},
            };
            // Only update stores that have a cluster assignment
            const clusterAssortmentStoresWithValidCluster = clusterAssortmentStores.filter(
                ({ clusterId }) => clusterId !== 'mock_cluster_id'
            );
            const clusterIds = uniq(clusterAssortmentStoresWithValidCluster.map(({ clusterId }) => clusterId));
            await assortmentService.updateClustersAssortmentRecords(
                { ...mainFilters },
                {
                    action,
                    materialCode,
                    clusterIds,
                }
            );

            dispatch(updateClusterAssortmentRecordsSuccess(action, materialCode, clusterAssortmentStoresWithValidCluster));
        } catch (err: unknown) {
            dispatch(updateClusterAssortmentRecordsFailure(err as Error, action, materialCode, clusterAssortmentStores));
        }
    };

export const addToAssortmentRecordFailure = (error: Error, assortmentRecord: AssortmentRecord, clusterId: string) =>
    ({ type: ADD_ASSORTMENT_RECORD_FAILURE, error, assortmentRecord, clusterId } as const);

export type AddAssortmentRecordFailureAction = ReturnType<typeof addToAssortmentRecordFailure>;

export const addToAssortmentRecordSuccess = (assortmentRecord: AssortmentRecord, clusterId: string) =>
    ({ type: ADD_ASSORTMENT_RECORD_SUCCESS, assortmentRecord, clusterId } as const);

export type AddAssortmentRecordSuccessAction = ReturnType<typeof addToAssortmentRecordSuccess>;

export type AddAssortmentRecordAction = {
    type: typeof ADD_ASSORTMENT_RECORD;
    assortmentRecord: AssortmentRecord;
    clusterId: string;
};

export const addToAssortment = (assortmentRecord: AssortmentRecord, clusterId: string) => async (dispatch: AnyDispatch) => {
    try {
        dispatch({ type: ADD_ASSORTMENT_RECORD, assortmentRecord, clusterId });
        await assortmentService.addAssortmentRecord(assortmentRecord);
        dispatch(addToAssortmentRecordSuccess(assortmentRecord, clusterId));
    } catch (err: unknown) {
        dispatch(addToAssortmentRecordFailure(err as Error, assortmentRecord, clusterId));
    }
};

export const removeAssortmentRecordFailure = (error: Error, assortmentRecord: AssortmentRecord, clusterId?: string) =>
    ({ type: REMOVE_ASSORTMENT_RECORD_FAILURE, error, assortmentRecord, clusterId } as const);

export type RemoveAssortmentRecordFailureAction = ReturnType<typeof removeAssortmentRecordFailure>;

export const removeAssortmentRecordSuccess = (assortmentRecord: AssortmentRecord, clusterId?: string) =>
    ({ type: REMOVE_ASSORTMENT_RECORD_SUCCESS, assortmentRecord, clusterId } as const);

export type RemoveAssortmentRecordSuccessAction = ReturnType<typeof removeAssortmentRecordSuccess>;

export type RemoveAssortmentRecordAction = {
    type: typeof REMOVE_ASSORTMENT_RECORD;
    assortmentRecord: AssortmentRecord;
    clusterId?: string;
};

export const removeAssortment =
    (assortmentRecord: AssortmentRecord, clusterId?: string) => async (dispatch: AnyDispatch) => {
        try {
            dispatch({ type: REMOVE_ASSORTMENT_RECORD, assortmentRecord, clusterId });
            await assortmentService.removeAssortmentRecord(assortmentRecord);
            dispatch(removeAssortmentRecordSuccess(assortmentRecord, clusterId));
        } catch (err: unknown) {
            dispatch(removeAssortmentRecordFailure(err as Error, assortmentRecord, clusterId));
        }
    };

export type UpdateAssortmentMaterialAction = {
    type: typeof UPDATE_ASSORTMENT_MATERIAL;
    materialId: string;
    materialAttributes: Partial<Material>;
};

export const updateAssortmentMaterialFailure = (error: Error, materialId: string, material: Material) =>
    ({ type: UPDATE_ASSORTMENT_MATERIAL_FAILURE, error, materialId, material } as const);

export type UpdateAssortmentMaterialFailureAction = ReturnType<typeof updateAssortmentMaterialFailure>;

export const updateAssortmentMaterialSuccess = (materialId: string, materialAttributes: Partial<Material>) =>
    ({ type: UPDATE_ASSORTMENT_MATERIAL_SUCCESS, materialId, materialAttributes } as const);

export type UpdateAssortmentMaterialSuccessAction = ReturnType<typeof updateAssortmentMaterialSuccess>;

export const updateAssortmentMaterial =
    (materialId: string, material: Material, materialAttributes: Partial<Material>) => async (dispatch: AnyDispatch) => {
        try {
            dispatch({ type: UPDATE_ASSORTMENT_MATERIAL, materialId, materialAttributes });
            await materialService.patch(materialId, materialAttributes);

            dispatch(updateAssortmentMaterialSuccess(materialId, materialAttributes));
        } catch (err: unknown) {
            const materialUpdateError = err as Error;
            dispatch(updateAssortmentMaterialFailure(materialUpdateError, materialId, material));

            throw new Error(materialUpdateError.message);
        }
    };

export type AssortmentActions =
    | SetAssortmentDataAction
    | GetAssortmentMetaAction
    | GetAssortmentMetaSuccessAction
    | GetAssortmentMetaFailureAction
    | AddAssortmentRecordAction
    | AddAssortmentRecordFailureAction
    | AddAssortmentRecordSuccessAction
    | RemoveAssortmentRecordAction
    | RemoveAssortmentRecordFailureAction
    | RemoveAssortmentRecordSuccessAction
    | UpdateClusterAssortmentRecordsAction
    | UpdateClusterAssortmentRecordsFailureAction
    | UpdateClusterAssortmentRecordsSuccessAction
    | UpdateAssortmentMaterialAction
    | UpdateAssortmentMaterialFailureAction
    | UpdateAssortmentMaterialSuccessAction
    | UpdateAssortmentClusterFailureAction
    | UpdateAssortmentClusterSuccessAction
    | UpdateAssortmentClusterAction;
